import { useEffect, useState, useCallback } from "react";
import {
  getBlueDrafts, getBlueDraft, updateBlueDraft,
  approveBlueDraft, rejectBlueDraft, publishBlueDraft,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { TrustBadge } from "../components/TrustBadge";
import { toast } from "sonner";
import { Sparkles, Check, X, Send, Save, RefreshCw, FileText } from "lucide-react";

const STATUS_FILTERS = [
  { value: "", label: "Tous" },
  { value: "blue_prepared", label: "Préparés par Blue" },
  { value: "human_review", label: "En revue" },
  { value: "ready_to_publish", label: "Prêts à publier" },
  { value: "published", label: "Publiés" },
  { value: "rejected", label: "Rejetés" },
];

const STATUS_COLOR = {
  blue_prepared: "bg-sky-100 text-sky-700",
  human_review: "bg-amber-100 text-amber-700",
  ready_to_publish: "bg-emerald-100 text-emerald-700",
  published: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

export default function AdminBluePublishingPage() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBlueDrafts(statusFilter ? { status: statusFilter } : {});
      setDrafts(res.data?.drafts || []);
    } catch {
      toast.error("Impossible de charger les brouillons.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8" data-testid="admin-blue-publishing-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-accent" /> Publication assistée par Blue
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Revue des brouillons préparés par Blue. Blue ne publie jamais seul — toute mise en ligne exige une action humaine.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="blue-drafts-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Rafraîchir
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="blue-drafts-filters">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              statusFilter === f.value
                ? "bg-primary text-white border-primary"
                : "bg-white text-muted-foreground border-border hover:border-primary"
            }`}
            data-testid={`blue-drafts-filter-${f.value || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : drafts.length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-10 text-center text-muted-foreground" data-testid="blue-drafts-empty">
          <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
          Aucun brouillon pour ce filtre.
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden" data-testid="blue-drafts-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border bg-muted/40">
                <th className="py-3 px-4 font-medium">Titre</th>
                <th className="py-3 px-4 font-medium">Statut</th>
                <th className="py-3 px-4 font-medium">Blocs</th>
                <th className="py-3 px-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((d) => (
                <tr key={d.id} className="border-b border-border/60" data-testid={`blue-draft-row-${d.id}`}>
                  <td className="py-3 px-4 font-medium text-primary">{d.title}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[d.status] || "bg-gray-100 text-gray-600"}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">{(d.blocks || []).length}</td>
                  <td className="py-3 px-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => setSelected(d.id)} data-testid={`blue-draft-open-${d.id}`}>
                      Ouvrir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <DraftReviewPanel
          draftId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}

function DraftReviewPanel({ draftId, onClose, onChanged }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", subtitle: "", chapeau: "" });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBlueDraft(draftId);
      setDraft(res.data);
      setForm({
        title: res.data.title || "",
        subtitle: res.data.subtitle || "",
        chapeau: res.data.chapeau || "",
      });
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => { reload(); }, [reload]);

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      await reload();
      onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Action impossible.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const handleReject = () => {
    const reason = window.prompt("Motif du rejet (obligatoire) :");
    if (!reason || reason.trim().length < 3) {
      toast.error("Un motif d'au moins 3 caractères est requis.");
      return;
    }
    runAction(() => rejectBlueDraft(draftId, reason.trim()), "Brouillon rejeté.");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="blue-draft-panel">
      <div
        className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-primary">Revue du brouillon</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-primary" data-testid="blue-draft-panel-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading || !draft ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Titre</label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="blue-draft-title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Sous-titre</label>
                <Input value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} data-testid="blue-draft-subtitle" />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Chapeau</label>
                <Textarea rows={3} value={form.chapeau} onChange={(e) => setForm((f) => ({ ...f, chapeau: e.target.value }))} className="resize-none" data-testid="blue-draft-chapeau" />
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => runAction(() => updateBlueDraft(draftId, form), "Modifications enregistrées.")}
                data-testid="blue-draft-save"
              >
                <Save className="h-4 w-4 mr-1" /> Enregistrer les modifications
              </Button>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-primary mb-3">Blocs et flags de confiance</h3>
              <div className="space-y-3">
                {(draft.blocks || []).map((b, i) => (
                  <div key={i} className="border border-border rounded-md p-3" data-testid={`blue-draft-block-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {b.blue_source_type && <TrustBadge type={b.blue_source_type} review size="xs" />}
                      {b.edited_by_human && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                          Édité par un humain
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{b.text}</p>
                  </div>
                ))}
                {(draft.blocks || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Aucun bloc dans ce brouillon.</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              <Button
                size="sm"
                disabled={busy}
                onClick={() => runAction(() => approveBlueDraft(draftId), "Brouillon validé.")}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="blue-draft-approve"
              >
                <Check className="h-4 w-4 mr-1" /> Valider
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => runAction(() => publishBlueDraft(draftId), "Brouillon publié.")}
                className="bg-primary hover:bg-primary/90"
                data-testid="blue-draft-publish"
              >
                <Send className="h-4 w-4 mr-1" /> Publier
              </Button>
              <Button size="sm" variant="destructive" disabled={busy} onClick={handleReject} data-testid="blue-draft-reject">
                <X className="h-4 w-4 mr-1" /> Rejeter
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
